import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/layout/SeoHead';
import CTABand from '@/components/sections/CTABand';
import { BRAND, FOUNDER, SERVICES } from '@/data/site';
import { AUDIENCES, CITY_PAGES } from '@/data/locations';
import { buildOrganizationJsonLd } from '@/lib/entitySchema';
import { GraduationCap, HeartHandshake, Ribbon, BadgeCheck, Sparkles, MapPin } from 'lucide-react';

const CREDENTIALS = [
  { icon: GraduationCap, label: 'Purdue University Graduate' },
  { icon: HeartHandshake, label: 'Former Social Worker, 10+ years' },
  { icon: Ribbon, label: 'Two-Time Cancer Survivor' },
  { icon: BadgeCheck, label: 'WBE Certified, Woman-Owned' },
  { icon: BadgeCheck, label: 'MBE Certified, Latina-Owned' },
  { icon: MapPin, label: 'Columbus, Indiana. Community Advocate' },
];

// Community block. Every city links to its dedicated city page when available.
const CITY_LOOKUP = CITY_PAGES.reduce((acc, c) => {
  if (!acc[c.city]) acc[c.city] = `/${c.slug}`;
  return acc;
}, {});

const COMMUNITIES = [
  {
    name: 'Columbus',
    body: 'Our home. The headquarters of Ohana and the heart of Bartholomew County. We serve every neighborhood, from the historic east side to Tipton Lakes, and our crew lives here.',
  },
  {
    name: 'Seymour',
    body: 'A regular route from Columbus down US-31. Estate cleanouts, rental turnovers, and pre-listing prep with the same speed as a Columbus job.',
  },
  {
    name: 'Edinburgh',
    body: 'Right between Bartholomew and Johnson counties. Squarely in our service area. Property cleanouts, downsizing support, and donation routing through the Hearth Project.',
  },
  {
    name: 'Franklin',
    body: 'North up I-65 from Columbus. Franklin families, attorneys, and realtors get the same 24-to-48-hour scheduling as our Columbus neighbors.',
  },
  {
    name: 'Greenwood',
    body: 'A growing suburb that means a lot of moves, rental turnovers, and garage cleanouts. We work with Greenwood landlords on ongoing turnover programs.',
  },
  {
    name: 'Shelbyville',
    body: 'East along US-31. Junk removal, debris removal, and full-property cleanouts for Shelby County homeowners and investors.',
  },
  {
    name: 'North Vernon',
    body: 'East on US-50 into Jennings County. Estate cleanouts and inherited property clearing with the same care we bring to every job.',
  },
  {
    name: 'Greensburg',
    body: 'East on I-74 into Decatur County. Pre-listing prep, estate cleanouts, and rental turnovers on the same schedule as a Columbus job.',
  },
  {
    name: 'Bloomington',
    body: 'West on State Road 46. Senior downsizing, estate work, and inherited-property clearing for Monroe County families.',
  },
];

export default function About() {
  return (
    <>
      <SeoHead
        title="About Olga Ortega & Ohana Property & Transition Services | Columbus, IN"
        description="Founded by Olga Ortega, a Purdue graduate, former social worker, and two-time cancer survivor. Ohana is a woman-owned, MBE/WBE certified property transition company serving Columbus, Indiana and Central Indiana."
        canonical="https://ohanapropertyexperts.com/about"
        jsonLd={buildOrganizationJsonLd()}
      />

      {/* HERO */}
      <section className="bg-ohana-primary text-ohana-bg" data-testid="about-hero">
        <div className="container-ohana pt-24 pb-20 md:pt-32 md:pb-28">
          <p className="overline !text-ohana-accent">Our Founder</p>
          <h1 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
            Before Ohana, there was a career built around{' '}
            <span className="text-ohana-accent">helping people through hard moments.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-ohana-bg/80 leading-relaxed">
            Olga Ortega, founder of Ohana Property &amp; Transition Services. Purdue graduate. Former social worker. Two-time cancer survivor. Built this company because every property transition deserves a partner who understands what that property means.
          </p>
        </div>
      </section>

      {/* SECTION 1. Founder Story */}
      <section className="section-pad bg-ohana-bg" data-testid="about-founder-story">
        <div className="container-ohana grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <p className="overline">Section 1 · Founder Story</p>
            <h2 className="h-display text-3xl md:text-4xl mt-3 leading-tight">
              A career built around showing up for people.
            </h2>
            <div className="mt-7 space-y-5 text-lg leading-relaxed text-ohana-text">
              <p>
                For over a decade, Olga Ortega worked as a social worker in Central Indiana. Her job was to show up for people navigating the hardest moments of their lives. Loss, displacement, financial crisis, and family breakdown. She learned quickly that the hardest moments rarely happen in isolation. They come with practical consequences nobody prepares you for.
              </p>
              <p>One of those consequences was property.</p>
              <p>
                When a family loses a parent, inherits a home they have never seen, or helps a senior move into assisted living, someone has to deal with what is left behind. The furniture. The appliances. The accumulated weight of a lifetime. And most of the time nobody in that family has the time, the capacity, or the emotional bandwidth to handle it. Especially not while grieving.
              </p>
              <p>
                Olga saw this happen again and again across her career. She also saw how poorly the existing options served those families. Companies that moved too fast. Crews that felt like strangers. No one who understood that clearing a home is not just a logistics problem. It is a human one.
              </p>
              <p>
                She founded Ohana Property &amp; Transition Services because she believed there was a better way to show up for people in those moments.
              </p>
              <p>
                Today, Ohana serves families, realtors, estate executors, property managers, and senior care professionals across Columbus, Indiana and Central Indiana. We are a woman-owned, Latina-owned, MBE and WBE certified business. We approach every job the way Olga approached her career. With patience, with respect, and with the understanding that the property matters because the person matters.
              </p>
            </div>

            {/* Pull quote */}
            <figure
              className="mt-10 border-l-4 border-ohana-accent pl-6 py-2"
              data-testid="about-pullquote"
            >
              <blockquote className="font-serif text-2xl md:text-3xl text-ohana-primary leading-snug">
                &ldquo;I built Ohana because I knew there was a better way to show up for people during a property transition. One that treats every home like it belongs to someone we care about.&rdquo;
              </blockquote>
              <figcaption className="mt-3 text-sm font-medium text-ohana-text-muted">
                {FOUNDER.name}, {FOUNDER.jobTitle}
              </figcaption>
            </figure>

            {/* Credentials */}
            <div
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3"
              data-testid="about-credentials"
            >
              {CREDENTIALS.map((c) => (
                <div
                  key={c.label}
                  className="flex items-start gap-3 rounded-xl border border-ohana-border bg-ohana-surface px-4 py-3"
                >
                  <c.icon className="h-5 w-5 text-ohana-primary mt-0.5 flex-none" />
                  <span className="text-sm font-medium text-ohana-text">{c.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Founder portrait */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-ohana-border bg-white">
              <img
                src={FOUNDER.photo}
                alt={FOUNDER.photoAlt}
                className="w-full h-auto object-cover aspect-[4/5]"
                loading="lazy"
                data-testid="founder-portrait"
              />
              <div className="px-5 py-4 border-t border-ohana-border">
                <p className="text-sm font-semibold text-ohana-primary">{FOUNDER.name}</p>
                <p className="text-xs text-ohana-text-muted mt-0.5">
                  Founder &amp; Owner · {FOUNDER.identity}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2. Why Property Transitions Matter */}
      <section className="section-pad bg-ohana-surface" data-testid="about-why-transitions">
        <div className="container-ohana max-w-4xl">
          <p className="overline">Section 2 · Why Property Transitions Matter</p>
          <h2 className="h-display text-3xl md:text-4xl mt-3 leading-tight">
            A cleanout is never just a cleanout.
          </h2>
          <div className="mt-7 space-y-5 text-lg leading-relaxed text-ohana-text">
            <p>
              Most calls we get start with a sentence about logistics, like &ldquo;I need to clear this house out,&rdquo; and end with a sentence about life. Loss. Retirement. Divorce. A parent moving into memory care. An inherited property three states away. A foreclosure that ends a long chapter. Property transitions are rarely about the property.
            </p>
            <p>
              That is why the right partner matters. The wrong cleanout crew treats your situation like an estimate. The right one treats your situation like the human story it actually is. We communicate clearly, respect the pace you need to move at, and finish the job in a way that lets you take the next step in your life.
            </p>
            <p>
              Ohana exists to be that partner. For families clearing a parent&rsquo;s home. For seniors downsizing into the next chapter. For executors and attorneys working a probate timeline. For realtors and investors who need a vendor that consistently shows up. Whatever the transition is, our job is to make it lighter.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3. Who We Help */}
      <section className="section-pad bg-ohana-bg" data-testid="about-who-we-help">
        <div className="container-ohana">
          <div className="max-w-3xl">
            <p className="overline">Section 3 · Who We Help</p>
            <h2 className="h-display text-3xl md:text-4xl mt-3 leading-tight">
              Eight kinds of clients. One way of working.
            </h2>
            <p className="mt-5 text-ohana-text-muted text-base md:text-lg leading-relaxed">
              Whether you are clearing a home, settling an estate, prepping a listing, or running a portfolio of rentals, here is exactly how we show up for the situation you are in.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {AUDIENCES.map((a) => (
              <article
                key={a.title}
                className="card-default"
                data-testid={`audience-${a.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <h3 className="font-serif text-xl text-ohana-primary leading-snug">{a.title}</h3>
                <p className="mt-3 text-sm font-semibold text-ohana-text uppercase tracking-wide">
                  Challenge
                </p>
                <p className="mt-1 text-sm text-ohana-text-muted leading-relaxed">{a.challenge}</p>
                <p className="mt-4 text-sm font-semibold text-ohana-text uppercase tracking-wide">
                  Our approach
                </p>
                <p className="mt-1 text-sm text-ohana-text-muted leading-relaxed">{a.approach}</p>
              </article>
            ))}
          </div>

          {/* Internal service links so the About page is not a dead end */}
          <div className="mt-12 rounded-2xl bg-ohana-surface border border-ohana-border p-6 md:p-8">
            <p className="overline">Services We Offer</p>
            <ul className="mt-4 flex flex-wrap gap-2">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${s.slug}`}
                    className="inline-flex items-center rounded-full border border-ohana-border bg-white px-4 py-2 text-sm font-medium text-ohana-primary hover:bg-ohana-primary hover:text-white transition"
                    data-testid={`about-service-link-${s.slug}`}
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION 4. Communities We Serve */}
      <section className="section-pad bg-ohana-surface" data-testid="about-communities">
        <div className="container-ohana">
          <div className="max-w-3xl">
            <p className="overline">Section 4 · Communities We Serve</p>
            <h2 className="h-display text-3xl md:text-4xl mt-3 leading-tight">
              Central Indiana. Every named city, not just a radius.
            </h2>
            <p className="mt-5 text-ohana-text-muted text-base md:text-lg leading-relaxed">
              Ohana is a Columbus, Indiana company. From here we serve every city below on our regular route. Same scheduling, same crew, same family-first standard.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {COMMUNITIES.map((c) => {
              const href = CITY_LOOKUP[c.name];
              return (
                <article
                  key={c.name}
                  className="card-default"
                  data-testid={`community-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <h3 className="font-serif text-xl text-ohana-primary leading-snug">
                    {href ? (
                      <Link
                        to={href}
                        className="hover:underline"
                        data-testid={`community-link-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {c.name}, Indiana
                      </Link>
                    ) : (
                      <>{c.name}, Indiana</>
                    )}
                  </h3>
                  <p className="mt-3 text-sm text-ohana-text-muted leading-relaxed">{c.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 5. Community Commitment */}
      <section className="section-pad bg-ohana-bg" data-testid="about-community-commitment">
        <div className="container-ohana grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="overline">Section 5 · Community Commitment</p>
            <h2 className="h-display text-3xl md:text-4xl mt-3 leading-tight">
              The Hearth Project &amp; how we give back.
            </h2>
            <div className="mt-7 space-y-5 text-lg leading-relaxed text-ohana-text">
              <p>
                Every cleanout produces items that still have life left in them. Furniture, appliances, clothing, household goods. Through the <Link to="/hearth-project" className="text-ohana-primary font-semibold underline" data-testid="about-hearth-link">Hearth Project</Link>, those items are routed to Central Indiana nonprofits, shelters, and families in need before anything is disposed of. It is the part of every job we are proudest of.
              </p>
              <p>
                We also work to keep disposal responsible. Hazardous and prohibited items are flagged before we arrive. Everything else goes through the Bartholomew County Solid Waste Management District or appropriate licensed channel. CFC appliance recycling fees are passed through at cost. No markup.
              </p>
              <p>
                Beyond every job, Ohana invests in Columbus. Olga is active in the local business community as an MBE and WBE certified leader, and Ohana is a long-term partner with Indiana realtors, estate attorneys, senior care professionals, and community advocates building a better Central Indiana.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-ohana-primary text-ohana-bg p-8 md:p-10">
              <Sparkles className="h-10 w-10 text-ohana-accent" />
              <h3 className="mt-5 font-serif text-2xl md:text-3xl leading-tight">
                Every property transition deserves a compassionate, reliable partner.
              </h3>
              <p className="mt-4 text-ohana-bg/80 leading-relaxed">
                Send photos and the property address. We respond same day, scope the job, and can usually schedule within 24 to 48 hours across Central Indiana.
              </p>
              <Link
                to="/contact"
                className="mt-7 inline-flex items-center rounded-full bg-ohana-accent text-ohana-primary font-semibold px-6 py-3 hover:bg-white transition"
                data-testid="about-cta-quote"
              >
                Get My Free Quote
              </Link>
              <p className="mt-4 text-xs text-ohana-bg/65">
                Or call <a className="underline" href={`tel:${BRAND.phoneTel}`}>{BRAND.phone}</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <CTABand
        title="Ready to put Ohana to work?"
        subtitle="Send us photos and we'll send you a quote, usually the same day."
      />
    </>
  );
}
